// src/components/CancelAccount.js - Updated with improved mobile responsiveness and user flow
import React, { useState, useEffect } from 'react';
import { UserMinus, AlertCircle, Lock, Loader, ArrowDown } from 'lucide-react';
import { auth, db } from '../firebase';
import { 
  deleteUser, 
  EmailAuthProvider, 
  reauthenticateWithCredential,
  GoogleAuthProvider,
  reauthenticateWithPopup 
} from 'firebase/auth';
import { doc, deleteDoc, updateDoc, getDoc } from 'firebase/firestore';
import styles from './CancelAccount.module.css';

const CancelAccount = ({ userId, tierType }) => {
  const [password, setPassword] = useState('');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDowngradeModalOpen, setIsDowngradeModalOpen] = useState(false);
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const [remainingDays, setRemainingDays] = useState(0);
  // New state for goodbye screen
  const [showGoodbyeScreen, setShowGoodbyeScreen] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const isPro = tierType === 'pro';

  // Check if user is authenticated with Google and fetch Pro subscription details
  useEffect(() => {
    const checkAuthProvider = async () => {
      const user = auth.currentUser;
      if (user) {
        const googleProvider = user.providerData.some(
          provider => provider.providerId === 'google.com'
        );
        setIsGoogleUser(googleProvider);
        
        // If user is Pro, get their subscription start date
        if (isPro) {
          try {
            const userDocRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
              const data = userDoc.data();
              
              if (data.proStartDate) {
                // Calculate remaining days in current billing period (assuming monthly)
                const startDate = new Date(data.proStartDate);
                const endOfPeriod = new Date(startDate);
                endOfPeriod.setMonth(endOfPeriod.getMonth() + 1);
                
                const today = new Date();
                const remainingMs = endOfPeriod - today;
                const remainingDays = Math.max(0, Math.ceil(remainingMs / (1000 * 60 * 60 * 24)));
                
                setRemainingDays(remainingDays);
              }
            }
          } catch (error) {
            console.error('Error fetching subscription details:', error);
          }
        }
      }
    };
    
    checkAuthProvider();
  }, [userId, isPro]);

  const openModal = () => {
    setError('');
    setPassword('');
    setConfirmChecked(false);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };
  
  const openDowngradeModal = () => {
    setError('');
    setConfirmChecked(false);
    setIsDowngradeModalOpen(true);
  };
  
  const closeDowngradeModal = () => {
    setIsDowngradeModalOpen(false);
  };

  const redirectToHomepage = () => {
    window.location.href = 'https://linkist.se';
  };

  const handleCancelAccount = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!confirmChecked) {
      setError('Please confirm that you want to cancel your account.');
      setLoading(false);
      return;
    }
    
    // Show goodbye screen immediately and start deletion in background
    setShowGoodbyeScreen(true);
    
    try {
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('User not authenticated.');
      }

      // Re-authenticate based on provider type
      if (isGoogleUser) {
        // For Google users, re-authenticate with Google
        try {
          const provider = new GoogleAuthProvider();
          await reauthenticateWithPopup(user, provider);
          console.log('Successfully re-authenticated with Google');
        } catch (googleAuthError) {
          console.error('Google re-authentication error:', googleAuthError);
          setShowGoodbyeScreen(false); // Hide goodbye screen if auth fails
          setError('Authentication failed. Please try again.');
          setLoading(false);
          return;
        }
      } else {
        // For email/password users, use password
        if (!password) {
          setShowGoodbyeScreen(false); // Hide goodbye screen if auth fails
          setError('Password is required to cancel your account.');
          setLoading(false);
          return;
        }
        
        try {
          const credential = EmailAuthProvider.credential(user.email, password);
          await reauthenticateWithCredential(user, credential);
        } catch (authError) {
          console.error('Re-authentication error:', authError);
          setShowGoodbyeScreen(false); // Hide goodbye screen if auth fails
          
          if (authError.code === 'auth/wrong-password') {
            setError('Incorrect password. Please try again.');
          } else {
            setError('Authentication failed. Please try again.');
          }
          setLoading(false);
          return;
        }
      }

      // If user is on pro tier, handle subscription cancellation.
      if (isPro) {
        console.log('Pro user account - subscription would be cancelled here');
        // Add your payment provider cancellation code here
      }

      // First, get the user's username before deleting the user document
      const userDocRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userDocRef);
      const username = userSnap.exists() ? userSnap.data().username : null;

      // Delete from users collection
      await deleteDoc(userDocRef);
      
      // Delete the username mapping if it exists
      if (username) {
        console.log(`Deleting username mapping for: ${username}`);
        await deleteDoc(doc(db, 'usernames', username));
      }
      
      // Delete from commit collection if exists
      try {
        await deleteDoc(doc(db, 'commit', userId));
      } catch (commitError) {
        console.log('No commit document to delete or error:', commitError);
      }

      // Delete the Firebase Auth account
      await deleteUser(user);
      
      // Set success state
      setDeleteSuccess(true);
      
      // Redirect after 5 seconds
      setTimeout(() => {
        window.location.href = 'https://linkist.se';
      }, 5000);
      
    } catch (error) {
      console.error('Error cancelling account:', error);
      setShowGoodbyeScreen(false); // Hide goodbye screen if process fails
      
      if (error.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later.');
      } else if (error.code === 'auth/requires-recent-login') {
        setError('For security, please sign out and sign in again before cancelling your account.');
      } else if (error.code === 'auth/popup-closed-by-user') {
        setError('Authentication popup was closed. Please try again.');
      } else if (error.code === 'auth/cancelled-popup-request') {
        setError('Authentication process was cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Authentication popup was blocked by your browser. Please allow popups for this site.');
      } else {
        setError('Error cancelling account: ' + error.message);
      }
      setLoading(false);
    }
  };
  
  // Handle downgrade from Pro to Free
  const handleDowngradeSubscription = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!confirmChecked) {
      setError('Please confirm that you want to downgrade your subscription.');
      setLoading(false);
      return;
    }

    try {
      // Update the user's tierType to "free" in Firestore
      const userDocRef = doc(db, 'users', userId);
      
      await updateDoc(userDocRef, {
        tierType: 'free',
        proEndDate: new Date().toISOString()  // Record when they downgraded
      });
      
      // Show success message and close modal
      alert('Your subscription has been downgraded to Free. You can continue to use Pro features until the end of your billing period.');
      closeDowngradeModal();
      
      // Reload the page to reflect the changes
      window.location.reload();
      
    } catch (error) {
      console.error('Error downgrading subscription:', error);
      setError('An error occurred while downgrading your subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // If showing goodbye screen, render that instead of regular component
  if (showGoodbyeScreen) {
    return (
      <div className={styles.goodbyeContainer}>
        <div className={styles.goodbyeContent}>
          <h2>Thank you for trying Linkist.se</h2>
          {deleteSuccess ? (
            <>
              <p>Your account has been successfully deleted.</p>
              <p>We hope to see you again in the future!</p>
              <p className={styles.redirectMessage}>You will be redirected in a few seconds...</p>
            </>
          ) : (
            <>
              <p>We're processing your account deletion...</p>
              <div className={styles.loaderContainer}>
                <Loader size={32} className={styles.loadingIcon} />
              </div>
            </>
          )}
          <button 
            className={styles.homeButton} 
            onClick={redirectToHomepage}
          >
            Return to Homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.cancelAccountContainer}>
      <div className={styles.sectionContent}>
        
        {/* Pro users see both downgrade and cancellation options */}
        {isPro && (
          <>
            <div className={styles.infoBox}>
              <ArrowDown size={24} className={styles.infoIcon} style={{ color: '#255716c4' }} />
              <div className={styles.infoBoxContent}>
                <h3>Downgrade to Free</h3>
                <p>You can downgrade to the Free tier and keep your account.</p>
                <p className={styles.proInfo}>
                  You can continue to use Pro features until the end of your current billing period
                  {remainingDays > 0 && ` (${remainingDays} days remaining)`}.
                </p>
              </div>
            </div>
            
            <button className={styles.downgradeButton} onClick={openDowngradeModal}>
              <ArrowDown size={18} />
              Downgrade to Free
            </button>
            
            <div className={styles.separator}></div>
          </>
        )}
        
        {/* All users see the Cancel Account option */}
        <div className={styles.infoBox}>
          <AlertCircle size={24} className={styles.infoIcon} />
          <div className={styles.infoBoxContent}>
            <h3>Cancel Your Account</h3>
            <p>Cancelling your account will permanently delete all your profile data and cannot be undone.</p>
            {isPro && (
              <p className={styles.proWarning}>
                Your pro subscription will also be cancelled and you will no longer be charged.
              </p>
            )}
          </div>
        </div>
        
        <button className={styles.cancelButton} onClick={openModal}>
          <UserMinus size={18} />
          Cancel My Account
        </button>
      </div>

      {/* Downgrade Modal */}
      {isDowngradeModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h2>Downgrade to Free</h2>
            
            <div className={styles.downgradeInfo}>
              <p>You're about to downgrade from Pro to Free tier.</p>
              <p>You'll continue to have access to Pro features until the end of your current billing period.</p>
              <p>After downgrading, you'll have limited features:</p>
              <ul className={styles.featureList}>
                <li>Maximum of 5 links (any extra links will be hidden)</li>
                <li>Basic themes only</li>
                <li>Basic pallates only</li>
                <li>No link ordering, pinning, ghosting or hiding</li>
                <li>No ghost mode or link shortener</li>
                <li>Maximum of 5 icons</li>
                <li>No custom color for icons</li>
                <li>No click tracking</li>
              </ul>
            </div>
            
            <form onSubmit={handleDowngradeSubscription}>
              <div className={styles.confirmCheckbox}>
                <input
                  type="checkbox"
                  id="confirmDowngrade"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                />
                <label htmlFor="confirmDowngrade">
                  I understand I will lose access to Pro features at the end of my billing period
                </label>
              </div>
              
              {error && <div className={styles.errorMessage}>{error}</div>}
              
              <div className={styles.modalButtons}>
                <button 
                  type="button" 
                  onClick={closeDowngradeModal}
                  className={styles.cancelModalButton}
                  disabled={loading}
                >
                  Keep Pro Subscription
                </button>
                
                <button 
                  type="submit" 
                  className={styles.downgradeConfirmButton}
                  disabled={loading || !confirmChecked}
                >
                  {loading ? (
                    <>
                      <Loader size={16} className={styles.loadingIcon} /> Processing...
                    </>
                  ) : (
                    <>
                      <ArrowDown size={16} /> Confirm Downgrade
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cancel Account Modal */}
      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h2>We're sorry to see you go</h2>
            
            <form onSubmit={handleCancelAccount}>
              {isGoogleUser ? (
                <div className={styles.googleAuthMessage}>
                  <p>You're signed in with Google. When you click "Permanently Delete Account", you'll need to confirm your identity.</p>
                </div>
              ) : (
                <div className={styles.passwordField}>
                  <Lock size={18} className={styles.passwordIcon} />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password to confirm"
                    className={styles.passwordInput}
                  />
                </div>
              )}
              
              <div className={styles.confirmCheckbox}>
                <input
                  type="checkbox"
                  id="confirmCancel"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                />
                <label htmlFor="confirmCancel">
                  I understand this will permanently delete my account and all my data
                </label>
              </div>
              
              {error && <div className={styles.errorMessage}>{error}</div>}
              
              <div className={styles.modalButtons}>
                <button 
                  type="button" 
                  onClick={closeModal}
                  className={styles.cancelModalButton}
                  disabled={loading}
                >
                  Keep My Account
                </button>
                
                <button 
                  type="submit" 
                  className={styles.confirmCancelButton}
                  disabled={loading || !confirmChecked}
                >
                  {loading ? (
                    <>
                      <Loader size={16} className={styles.loadingIcon} /> Processing...
                    </>
                  ) : (
                    <>
                      <UserMinus size={16} /> Permanently Delete Account
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CancelAccount;